import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../Config";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Space,
  Typography,
  Modal,
  Descriptions,
  Layout,
  Input,
  Select,
  Row,
  Col,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  CalendarOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { EyeOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import Sidebar from "../../../Components/Sidebar";
import { MenuOutlined } from "@ant-design/icons";
import UserViolationsModal from "./UserViolationsModal";

const { Header, Content } = Layout;
const { Title, Paragraph, Text } = Typography;

const AllUserPage = () => {
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");
  const [mediaLinkStatus, setMediaLinkStatus] = useState("");
  const [variationSort, setVariationSort] = useState("");

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [viewOpen, setViewOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [violationsUser, setViolationsUser] = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const fetchUsers = async (
    page = pagination.current,
    limit = pagination.pageSize,
    searchValue = search,
    paymentValue = paymentStatus,
    mediaValue = mediaLinkStatus,
    variationSortValue = variationSort,
  ) => {
    try {
      const token = sessionStorage.getItem("token");
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          limit,
          search: searchValue || undefined,
          paymentStatus: paymentValue || undefined,
          mediaLinkStatus: mediaValue || undefined,
          sortBy: variationSortValue ? "variationCount" : undefined,
          sortOrder: variationSortValue || undefined,
        },
      });

      const { users, totalUsers, currentPage } = res.data.data;

      setUsers(users);
      setPagination((prev) => ({
        ...prev,
        current: currentPage,
        total: totalUsers,
      }));
    } catch {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchUsers(1, pagination.pageSize);
    }, 500);

    return () => clearTimeout(delay);
  }, [search, paymentStatus, mediaLinkStatus, variationSort]);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchUsers(1, pagination.pageSize);
    }, 500);

    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, paymentStatus, mediaLinkStatus]);

  const handleDelete = async (id) => {
    try {
      const token = sessionStorage.getItem("token");
      setLoading(true);

      await axios.delete(`${BASE_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("User deleted successfully");
      fetchUsers(pagination.current, pagination.pageSize);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to delete user");
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = async (record) => {
    try {
      const token = sessionStorage.getItem("token");
      setUserDetailsLoading(true);
      setViewOpen(true);

      const res = await axios.get(`${BASE_URL}/users/${record.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSelectedUser(res.data.data);
    } catch (error) {
      message.error("Failed to fetch user details");
      setViewOpen(false);
    } finally {
      setUserDetailsLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewOpen(false);
    setSelectedUser(null);
  };

  const handleTableChange = (pag) => {
    fetchUsers(pag.current, pag.pageSize);
  };

  const columns = [
    {
      title: "S.No",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Country",
      dataIndex: "country",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
    },
    {
      title: "Variations",
      dataIndex: "variationCount",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleString(),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => openViewModal(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => navigate(`/edit-user/${record.id}`)}
          />
          <Tooltip title="Violations / ban">
            <Button
              type="text"
              icon={<WarningOutlined style={{ color: "#fa8c16" }} />}
              onClick={() => setViolationsUser(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const MobileUserCard = ({ user }) => {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 12,
          padding: 16,
          marginBottom: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >
        {/* Top Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img
              src="https://i.pravatar.cc/40"
              alt=""
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
              }}
            />

            <div>
              <div style={{ fontWeight: 600 }}>{user.name}</div>

              <div style={{ fontSize: 12, color: "#888" }}>
                {user.email}
              </div>
            </div>
          </div>

          <div>
            <span
              style={{
                background:
                  user.variationCount > 0 ? "#e6f4ea" : "#fdecea",

                color:
                  user.variationCount > 0 ? "#28a745" : "#dc3545",

                padding: "4px 10px",
                borderRadius: 20,
                fontSize: 12,
                fontWeight: 500,
              }}
            >
              {user.variationCount} Variations
            </span>
          </div>
        </div>

        {/* Details */}

        <div style={{ fontSize: 13, marginBottom: 4 }}>
          <b>Country:</b> {user.country}
        </div>

        <div style={{ fontSize: 13, marginBottom: 4 }}>
          <b>Mobile:</b> {user.mobileNumber}
        </div>

        <div style={{ fontSize: 13 }}>
          <b>Created:</b>{" "}
          {new Date(user.createdAt).toLocaleDateString()}
        </div>

        {/* Actions */}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 10,
            gap: 10,
          }}
        >
          <Button
            icon={<EyeOutlined />}
            onClick={() => openViewModal(user)}
          />

          <Button
            icon={<EditOutlined />}
            onClick={() => navigate(`/edit-user/${user.id}`)}
          />

          <Button
            icon={<WarningOutlined style={{ color: "#fa8c16" }} />}
            onClick={() => setViolationsUser(user)}
          />

          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(user.id)}
          />
        </div>
      </div>
    );
  };
  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f7fa" }}>
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        isMobile={isMobile}
        collapsedWidth="0"
        role="admin"
      />
      <div
        className="main-layout"
        style={{
          marginLeft: isMobile ? 0 : collapsed ? 0 : 240,
          transition: "0.3s",
          padding: isMobile ? 10 : 20,
        }}
      >
        <Header
          className="MainHeader"
        >
          {isMobile && (
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileOpen(true)}
              style={{ fontSize: 18, marginRight: 12 }}
            />
          )}
          <div style={{ flex: 1 }}>
            <Text style={{ fontSize: 20, fontWeight: "bold" }}>All Users</Text>
          </div>
        </Header>
        {
          isMobile ? (

            /* ✅ MOBILE FILTER DESIGN */

            <div style={{ marginBottom: 16 }}>

              {/* Search */}
              <Input
                placeholder="Search"
                prefix={<SearchOutlined />}
                size="large"
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  borderRadius: 10,
                  marginBottom: 10,
                }}
              />

              {/* Filter row */}
              <div
                style={{
                  display: "flex",
                  gap: 10,
                }}
              >

                {/* Payment Status */}
                <Select
                  placeholder="Payment"
                  style={{ flex: 1 }}
                  value={paymentStatus || undefined}
                  onChange={(value) => setPaymentStatus(value || "")}
                  options={[
                    { label: "Paid", value: "paid" },
                    { label: "Not Paid", value: "not_paid" },
                  ]}
                />

                {/* Media Link */}
                <Select
                  placeholder="Media"
                  style={{ flex: 1 }}
                  value={mediaLinkStatus || undefined}
                  onChange={(value) => setMediaLinkStatus(value || "")}
                  options={[
                    { label: "Active", value: "active" },
                    { label: "Expired", value: "expired" },
                    { label: "Disabled", value: "disabled" },
                  ]}
                />

                {/* Sort */}
                <Select
                  placeholder="Sort"
                  style={{ flex: 1 }}
                  value={variationSort || undefined}
                  onChange={(value) => setVariationSort(value || "")}
                  options={[
                    { label: "Low → High", value: "asc" },
                    { label: "High → Low", value: "desc" },
                  ]}
                />

              </div>

            </div>

          ) : (

            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>

              <Col xs={24} sm={24} md={12} lg={8}>
                <Input.Search
                  placeholder="Search name, email, mobile"
                  allowClear
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  size="large"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={5}>
                <Select
                  placeholder="Payment Status"
                  allowClear
                  size="large"
                  style={{ width: "100%" }}
                  value={paymentStatus || undefined}
                  onChange={(value) => setPaymentStatus(value || "")}
                  options={[
                    { label: "Paid", value: "paid" },
                    { label: "Not Paid", value: "not_paid" },
                  ]}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={5}>
                <Select
                  placeholder="Media Link Status"
                  allowClear
                  size="large"
                  style={{ width: "100%" }}
                  value={mediaLinkStatus || undefined}
                  onChange={(value) => setMediaLinkStatus(value || "")}
                  options={[
                    { label: "Active", value: "active" },
                    { label: "Expired", value: "expired" },
                    { label: "Disabled", value: "disabled" },
                  ]}
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Select
                  placeholder="Sort Variations"
                  allowClear
                  size="large"
                  style={{ width: "100%" }}
                  value={variationSort || undefined}
                  onChange={(value) => setVariationSort(value || "")}
                  options={[
                    { label: "Low → High", value: "asc" },
                    { label: "High → Low", value: "desc" },
                  ]}
                />
              </Col>
            </Row>
          )
        }

        {
          isMobile ? (
            <>
              {users.map((user) => (
                <MobileUserCard key={user.id} user={user} />
              ))}

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <Button
                  onClick={() =>
                    fetchUsers(pagination.current + 1)
                  }
                >
                  Load More
                </Button>
              </div>
            </>
          ) : (
            <Table
              columns={columns}
              dataSource={users}
              rowKey="id"
              loading={loading}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 900 }}
            />
          )
        }

        <Modal
          open={viewOpen}
          title="User Details"
          onCancel={closeViewModal}
          footer={null}
          width={800}
        >
          {userDetailsLoading ? (
            <p>Loading...</p>
          ) : (
            selectedUser && (
              <>
                <Descriptions bordered column={1} style={{ marginBottom: 20 }}>
                  <Descriptions.Item label="Name">
                    {selectedUser.name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedUser.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Country">
                    {selectedUser.country}
                  </Descriptions.Item>
                  <Descriptions.Item label="Mobile Number">
                    {selectedUser.mobileNumber}
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">
                    {new Date(selectedUser.createdAt).toLocaleString()}
                  </Descriptions.Item>
                </Descriptions>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h3>User Variations</h3>
                  <Button
                    icon={<WarningOutlined />}
                    onClick={() => setViolationsUser({ id: selectedUser.id || selectedUser._id, name: selectedUser.name })}
                  >
                    Violations / ban
                  </Button>
                </div>

                <Table
                  dataSource={selectedUser.variations || []}
                  rowKey="variationId"
                  pagination={false}
                  columns={[
                    {
                      title: "Image",
                      dataIndex: "image",
                      render: (img) => (
                        <img
                          src={img}
                          alt="asana"
                          style={{
                            width: 60,
                            height: 60,
                            objectFit: "cover",
                            borderRadius: 6,
                          }}
                        />
                      ),
                    },
                    {
                      title: "English",
                      dataIndex: "english",
                    },
                    {
                      title: "Sanskrit",
                      dataIndex: "sanskrit",
                    },
                    {
                      title: "Payment Status",
                      dataIndex: "paymentStatus",
                      render: (status) => (
                        <span
                          style={{
                            color: status === "paid" ? "green" : "red",
                            fontWeight: 500,
                          }}
                        >
                          {status}
                        </span>
                      ),
                    },
                    {
                      title: "Media Link Status",
                      dataIndex: "mediaLinkStatus",
                      render: (status) => (
                        <span
                          style={{
                            color:
                              status === "active"
                                ? "green"
                                : status === "expired"
                                  ? "orange"
                                  : "red",
                            fontWeight: 500,
                          }}
                        >
                          {status}
                        </span>
                      ),
                    },
                    {
                      title: "Created At",
                      dataIndex: "createdAt",
                      render: (date) => new Date(date).toLocaleString(),
                    },
                  ]}
                />
              </>
            )
          )}
        </Modal>

        <UserViolationsModal
          open={!!violationsUser}
          user={violationsUser}
          onClose={() => setViolationsUser(null)}
        />
      </div>
    </Layout>
  );
};

export default AllUserPage;
